/**
 * API Client for Agent Management
 * Handles CRUD operations for Windows Agents
 */

import axios from 'axios';

const API_BASE_URL = `${process.env.REACT_APP_API_URL || ''}/api/v1`;

// Helper to get auth token (consistent with rest of app)
const getAuthToken = () => localStorage.getItem('access_token');

/**
 * Get all agents
 * @returns {Promise<Array>} List of agents
 */
export const getAgents = async () => {
  const response = await axios.get(`${API_BASE_URL}/agents`, {
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`
    }
  });
  return response.data;
};

/**
 * Get agent by ID
 * @param {string} agentId - Agent UUID
 * @returns {Promise<Object>} Agent details
 */
export const getAgentById = async (agentId) => {
  const response = await axios.get(`${API_BASE_URL}/agents/${agentId}`, {
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`
    }
  });
  return response.data;
};

/**
 * Register new agent
 * @param {Object} agentData - Agent registration data
 * @param {string} agentData.uid - Agent UID (from MSI installation)
 * @param {string} agentData.hostname - Windows hostname
 * @param {string} agentData.name - Friendly name for agent
 * @returns {Promise<Object>} Registered agent with HMAC secret
 */
export const registerAgent = async (agentData) => {
  const response = await axios.post(`${API_BASE_URL}/agents/register`, agentData, {
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json'
    }
  });
  return response.data;
};

/**
 * Update agent configuration
 * @param {string} agentId - Agent UUID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated agent
 */
export const updateAgent = async (agentId, updates) => {
  const response = await axios.put(`${API_BASE_URL}/agents/${agentId}`, updates, {
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json'
    }
  });
  return response.data;
};

/**
 * Delete agent
 * @param {string} agentId - Agent UUID
 * @returns {Promise<void>}
 */
export const deleteAgent = async (agentId) => {
  await axios.delete(`${API_BASE_URL}/agents/${agentId}`, {
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`
    }
  });
};

/**
 * Get agent status (online/offline/busy)
 * @param {string} agentId - Agent UUID
 * @returns {Promise<Object>} Status info
 */
export const getAgentStatus = async (agentId) => {
  const response = await axios.get(`${API_BASE_URL}/agents/${agentId}/status`, {
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`
    }
  });
  return response.data;
};

/**
 * Get agent jobs history
 * @param {string} agentId - Agent UUID
 * @param {Object} params - Query parameters (limit, offset, status)
 * @returns {Promise<Object>} Jobs list with pagination
 */
export const getAgentJobs = async (agentId, params = {}) => {
  const response = await axios.get(`${API_BASE_URL}/agents/${agentId}/jobs`, {
    params,
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`
    }
  });
  return response.data;
};

/**
 * Get agent heartbeats (metrics history)
 * @param {string} agentId - Agent UUID
 * @param {Object} params - Query parameters (hours, limit)
 * @returns {Promise<Array>} Heartbeat records
 */
export const getAgentHeartbeats = async (agentId, params = { hours: 24 }) => {
  const response = await axios.get(`${API_BASE_URL}/agents/${agentId}/heartbeats`, {
    params,
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`
    }
  });
  return response.data;
};

/**
 * Send control command to agent
 * @param {string} agentId - Agent UUID
 * @param {string} action - Command action (ping, restart, stop_job, update_config)
 * @param {Object} params - Command parameters
 * @returns {Promise<Object>} Command result
 */
export const sendAgentCommand = async (agentId, action, params = {}) => {
  const response = await axios.post(
    `${API_BASE_URL}/agents/${agentId}/command`,
    { action, params },
    {
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json'
      }
    }
  );
  return response.data;
};

/**
 * Test agent connectivity (ping)
 * @param {string} agentId - Agent UUID
 * @returns {Promise<Object>} Ping result with latency
 */
export const pingAgent = async (agentId) => {
  return await sendAgentCommand(agentId, 'ping');
};

/**
 * Hard-disconnect an agent: mark disabled + shutdown process.
 * Agent will not reconnect until re-enabled.
 */
export const disconnectAgent = async (agentId) => {
  const response = await axios.post(
    `${API_BASE_URL}/agents/${agentId}/disconnect`,
    {},
    { headers: { 'Authorization': `Bearer ${getAuthToken()}` } }
  );
  return response.data;
};

/**
 * Re-enable a disabled agent so it can reconnect.
 */
export const enableAgent = async (agentId) => {
  const response = await axios.post(
    `${API_BASE_URL}/agents/${agentId}/enable`,
    {},
    { headers: { 'Authorization': `Bearer ${getAuthToken()}` } }
  );
  return response.data;
};

/**
 * Get agents statistics (dashboard)
 * @returns {Promise<Object>} Stats (total, online, offline, busy)
 */
export const getAgentsStats = async () => {
  const response = await axios.get(`${API_BASE_URL}/agents/stats/summary`, {
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`
    }
  });
  return response.data;
};

/**
 * Get agent installer information
 * @returns {Promise<Object>} Installer info (version, size, checksum, etc.)
 */
export const getInstallerInfo = async () => {
  const response = await axios.get(`${API_BASE_URL}/agents/installer/info`, {
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`
    }
  });
  return response.data;
};

/**
 * Get URL for downloading agent installer
 * @returns {string} Download URL
 */
export const getInstallerDownloadUrl = () => {
  return `${API_BASE_URL}/agents/installer/download`;
};

/**
 * Download agent installer (triggers browser download)
 * Calls /installer/windows which generates a fresh pre-configured ZIP.
 * @param {string|null} workspaceId - Optional workspace UUID
 */
export const downloadInstaller = (workspaceId = null) => {
  // Guard: workspaceId must be a string (not an event object)
  const wsId = workspaceId && typeof workspaceId === 'string' ? workspaceId : null;

  const token = getAuthToken();
  if (!token) {
    alert('Vous devez être connecté pour télécharger l\'installateur');
    return;
  }

  // Use direct URL download with token as query param to avoid fetch()/HTTP2 issues
  const params = new URLSearchParams({ _token: token });
  if (wsId) params.set('workspace_id', wsId);
  const url = `${API_BASE_URL}/agents/installer/windows?${params}`;

  // window.location.href is the most reliable Chrome download trigger —
  // avoids the silent block that happens with programmatic a.click()
  window.location.href = url;
};

/**
 * Get online agents for a specific workspace (for scan wizard agent selection)
 * @param {string} workspaceId - Workspace UUID (optional, returns all if not specified)
 * @returns {Promise<Array>} List of online agents
 */
export const getOnlineAgents = async (workspaceId = null) => {
  const params = { status: 'online' };
  if (workspaceId) params.workspace_id = workspaceId;

  const response = await axios.get(`${API_BASE_URL}/agents`, {
    params,
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`
    }
  });
  return response.data;
};

/**
 * Download pre-configured agent installer for a specific agent
 * @param {string} agentId - Agent UUID
 */
export const downloadConfiguredInstaller = async (agentId) => {
  const token = getAuthToken();
  if (!token) {
    alert('Vous devez être connecté pour télécharger l\'installateur');
    return;
  }

  const url = `${API_BASE_URL}/agents/installer/download/${agentId}`;

  try {
    const response = await fetch(url, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      if (response.status === 404) {
        alert('L\'installateur n\'est pas disponible. Contactez le support.');
      } else {
        alert(`Erreur lors du téléchargement (${response.status})`);
      }
      return;
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = `SYLinkAgent-${agentId.slice(0, 8)}-setup.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Download failed:', error);
    alert('Erreur de téléchargement. Vérifiez votre connexion.');
  }
};

export default {
  getAgents,
  getAgentById,
  registerAgent,
  updateAgent,
  deleteAgent,
  getAgentStatus,
  getAgentJobs,
  getAgentHeartbeats,
  sendAgentCommand,
  pingAgent,
  disconnectAgent,
  enableAgent,
  getAgentsStats,
  getInstallerInfo,
  getInstallerDownloadUrl,
  downloadInstaller,
  getOnlineAgents,
  downloadConfiguredInstaller
};
